import './App.css';

import React, { lazy, useState, useMemo, Suspense } from 'react';
import { useTranslation } from 'react-i18next';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from 'react-query';
import { HelmetProvider, Helmet } from 'react-helmet-async';

import { GoogleOAuthProvider } from '@react-oauth/google';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterLuxon } from '@mui/x-date-pickers/AdapterLuxon';

import LoggedIn from './components/LoggedInWrapper';
import SnackBar from './components/SnackBar';
import Dialog from './components/Dialog';
import UploadDialog from './components/upload/UploadDialog';
import NPSDialog from './components/common/NPSDialog';
import Skeleton from './components/Skeleton';
import FlowRunsSnackbar from './components/flow/FlowRunsSnackbar';

import AppContext from './contexts/app';

import Login from './views/Login';
import Signup from './views/Signup';
import Onboarding from './views/Onboarding';
import ForgotPassword from './views/ForgotPassword';
import TrialEnded from './views/TrialEnded';

import { LightProvider } from './themes';

const Home = lazy(() => import('./views/Home'));
const CalendarSync = lazy(() => import('./views/CalendarSync'));
const Projects = lazy(() => import('./views/Projects'));
const Transcript = lazy(() => import('./views/Transcript'));
const Document = lazy(() => import('./views/Document'));
const Profile = lazy(() => import('./views/Profile'));
const Support = lazy(() => import('./views/Support'));
const Billing = lazy(() => import('./views/Billing'));

const Users = lazy(() => import('./views/Users'));
const User = lazy(() => import('./views/User'));
const NameList = lazy(() => import('./views/NameList'));
const TranscribeMode = lazy(() => import('./views/TranscribeMode'));
const Redirect = lazy(() => import('./components/common/Redirect'));
const ResetPassword = lazy(() => import('./views/ResetPassword'));
const VerifyEmail = lazy(() => import('./views/VerifyEmail'));
const TwoFactorVerify = lazy(() => import('./views/TwoFactorVerify'));

const Workspaces = lazy(() => import('./views/Workspaces'));
const Workspace = lazy(() => import('./views/Workspace'));
const WorkspaceModules = lazy(() => import('./views/WorkspaceModules'));
const Folder = lazy(() => import('./views/Folder'));
const Search = lazy(() => import('./views/Search'));

// Single Tenant Admin views
const SingleTenantUsers = lazy(() => import('./views/SingleTenant/Users'));
const SingleTenantUser = lazy(() => import('./views/SingleTenant/User'));
const SingleTenantWorkspaces = lazy(() => import('./views/SingleTenant/Workspaces'));
const SingleTenantWorkspace = lazy(() => import('./views/SingleTenant/Workspace'));

const Bookings = lazy(() => import('./views/booking/Bookings'));
const GuestBooking = lazy(() => import('./views/booking/GuestBooking'));
const CreateBooking = lazy(() => import('./views/booking/CreateBooking'));
const EditBooking = lazy(() => import('./views/booking/EditBooking'));

const WorkspaceAdmin = lazy(() => import('./views/WorkspaceAdmin'));
const WorkspaceInvite = lazy(() => import('./views/WorkspaceInvite'));
const UserInvite = lazy(() => import('./views/UserInvite.js'));

const Latest = lazy(() => import('./views/Latest'));
const Referral = lazy(() => import('./views/Referral'));

const Thread = lazy(() => import('./views/Thread'));
const ThreadAdmin = lazy(() => import('./views/ThreadAdmin'));
const Template = lazy(() => import('./views/Template'));
const CaseTemplate = lazy(() => import('./views/CaseTemplate'));

const File = lazy(() => import('./views/File'));
const Case = lazy(() => import('./views/Case'));
const Flows = lazy(() => import('./views/Flows'));
const Flow = lazy(() => import('./views/Flow'));
const FlowTrigger = lazy(() => import('./views/FlowTrigger'));
const FlowTemplates = lazy(() => import('./views/FlowTemplates'));

const SecureLoginVerify = lazy(() => import('./views/secure-login/Email'));
const SecureLoginSetup = lazy(() => import('./views/secure-login/Setup'));

const Customers = lazy(() => import('./views/Customers'));
const Customer = lazy(() => import('./views/Customer'));

const Integrations = lazy(() => import('./views/Integrations'));

const queryClient = new QueryClient();

function App() {
  const { i18n } = useTranslation();
  const [uploadDialog, setUploadDialog] = useState({ open: false, type: null });
  const [npsDialog, setNPSDialog] = useState({
    open: false,
    type: null,
    score: null,
  });

  const lang = useMemo(() => i18n.language, [i18n.language]);

  return (
    <QueryClientProvider client={queryClient}>
      <LightProvider>
        <HelmetProvider>
          <Helmet>
            <html lang={lang} />
          </Helmet>
          <LocalizationProvider
            dateAdapter={AdapterLuxon}
            adapterLocale={i18n.language}
          >
            <GoogleOAuthProvider
              clientId={process.env.REACT_APP_GOOGLE_CLIENT_ID}
            >
              <AppContext.Provider
                value={{
                  uploadDialog,
                  setUploadDialog,
                  npsDialog,
                  setNPSDialog,
                }}
              >
                <Router>
                  <Suspense fallback={<Skeleton />}>
                    <Routes>
                      <Route
                        path="/"
                        element={
                          <LoggedIn>
                            <Home />
                          </LoggedIn>
                        }
                      />
                      <Route
                        path="/projects"
                        element={
                          <LoggedIn>
                            <Projects />
                          </LoggedIn>
                        }
                      />
                      <Route path="/login" element={<Login />} />
                      <Route path="/trial-ended" element={<TrialEnded />} />
                      <Route path="/signup" element={<Signup />} />
                      <Route path="/referral/:token" element={<Signup />} />
                      <Route
                        path="/forgot-password"
                        element={<ForgotPassword />}
                      />
                      <Route
                        path="/reset-password/:token"
                        element={<ResetPassword />}
                      />
                      <Route
                        path="/verify-email/:token"
                        element={<VerifyEmail />}
                      />
                      <Route path="/2fa-verify" element={<TwoFactorVerify />} />
                      <Route
                        path="/guest-booking/:id"
                        element={<GuestBooking />}
                      />
                      <Route
                        path="/meeting/:id"
                        element={
                          <LoggedIn>
                            <Redirect to="/transcript/:id" />
                          </LoggedIn>
                        }
                      />
                      <Route
                        path="/meetings"
                        element={
                          <LoggedIn>
                            <CalendarSync />
                          </LoggedIn>
                        }
                      />
                      <Route
                        path="/transcript/:id"
                        element={
                          <LoggedIn allowGuests>
                            <Transcript />
                          </LoggedIn>
                        }
                      />
                      <Route
                        path="/file/:id"
                        element={
                          <LoggedIn>
                            <File />
                          </LoggedIn>
                        }
                      />
                      {/*
                      <Route
                        path="/cases/:id"
                        element={
                          <LoggedIn>
                            <Case />
                          </LoggedIn>
                        }
                      />
                      */}
                      <Route
                        path="/profile/billing"
                        element={
                          <LoggedIn allowTrialEnded>
                            <Billing />
                          </LoggedIn>
                        }
                      />
                      <Route
                        path="/profile"
                        element={
                          <LoggedIn>
                            <Profile />
                          </LoggedIn>
                        }
                      />
                      <Route
                        path="/booking"
                        element={
                          <LoggedIn>
                            <Bookings />
                          </LoggedIn>
                        }
                      />
                      <Route
                        path="/booking/new"
                        element={
                          <LoggedIn>
                            <CreateBooking />
                          </LoggedIn>
                        }
                      />
                      <Route
                        path="/booking/:id/edit"
                        element={
                          <LoggedIn>
                            <EditBooking />
                          </LoggedIn>
                        }
                      />
                      <Route
                        path="/support"
                        element={
                          <LoggedIn>
                            <Support />
                          </LoggedIn>
                        }
                      />
                      <Route
                        path="/thread"
                        element={
                          <LoggedIn>
                            <Thread />
                          </LoggedIn>
                        }
                      />
                      <Route
                        path="/thread/:id"
                        element={
                          <LoggedIn>
                            <Thread />
                          </LoggedIn>
                        }
                      />
                      <Route
                        path="/thread-admin/:id"
                        element={
                          <LoggedIn>
                            <ThreadAdmin />
                          </LoggedIn>
                        }
                      />
                      <Route
                        path="/search"
                        element={
                          <LoggedIn>
                            <Search />
                          </LoggedIn>
                        }
                      />
                      <Route
                        path="/users"
                        element={
                          <LoggedIn requireSales>
                            <Users />
                          </LoggedIn>
                        }
                      />
                      <Route
                        path="/user/:id"
                        element={
                          <LoggedIn requireSales>
                            <User />
                          </LoggedIn>
                        }
                      />
                      <Route
                        path="/flow"
                        element={
                          <LoggedIn>
                            <Flows />
                          </LoggedIn>
                        }
                      />
                      <Route
                        path="/flow/:id"
                        element={
                          <LoggedIn>
                            <Flow />
                          </LoggedIn>
                        }
                      />
                      <Route
                        path="/flow-trigger/:id"
                        element={
                          <LoggedIn>
                            <FlowTrigger />
                          </LoggedIn>
                        }
                      />
                      <Route
                        path="/flow-templates"
                        element={
                          <LoggedIn>
                            <FlowTemplates />
                          </LoggedIn>
                        }
                      />
                      <Route
                        path="/document/:id"
                        element={
                          <LoggedIn>
                            <Document />
                          </LoggedIn>
                        }
                      />
                      <Route
                        path="/workspaces"
                        element={
                          <LoggedIn requireSales>
                            <Workspaces />
                          </LoggedIn>
                        }
                      />
                      <Route
                        path="/organization/:id"
                        element={
                          <LoggedIn requireSales>
                            <Workspace />
                          </LoggedIn>
                        }
                      />
                      <Route
                        path="/workspace/:id/modules"
                        element={
                          <LoggedIn requireSales>
                            <WorkspaceModules />
                          </LoggedIn>
                        }
                      />
                      <Route
                        path="/folders/*"
                        element={
                          <LoggedIn>
                            <Folder />
                          </LoggedIn>
                        }
                      />
                      <Route
                        path="/workspace/:id"
                        element={
                          <LoggedIn requireSales>
                            <Workspace />
                          </LoggedIn>
                        }
                      />
                      <Route
                        path="/workspace/admin"
                        element={
                          <LoggedIn>
                            <WorkspaceAdmin />
                          </LoggedIn>
                        }
                      />
                      <Route
                        path="/workspace/admin/members"
                        element={
                          <LoggedIn>
                            <WorkspaceAdmin />
                          </LoggedIn>
                        }
                      />
                      <Route
                        path="/workspace/admin/api-key"
                        element={
                          <LoggedIn>
                            <WorkspaceAdmin />
                          </LoggedIn>
                        }
                      />
                      <Route
                        path="/workspace/admin/meeting-bot-settings"
                        element={
                          <LoggedIn>
                            <WorkspaceAdmin />
                          </LoggedIn>
                        }
                      />
                      <Route
                        path="/workspace/:workspaceId/template/:templateId"
                        element={
                          <LoggedIn requireSales>
                            <Template />
                          </LoggedIn>
                        }
                      />
                      <Route
                        path="/workspace/:workspaceId/case-template/:caseTemplateId"
                        element={
                          <LoggedIn requireSales>
                            <CaseTemplate />
                          </LoggedIn>
                        }
                      />
                      <Route
                        path="/workspace/:workspaceId/flow/:id"
                        element={
                          <LoggedIn requireSales>
                            <Flow />
                          </LoggedIn>
                        }
                      />
                      <Route
                        path="/invite/:code"
                        element={
                          <LoggedIn>
                            <WorkspaceInvite />
                          </LoggedIn>
                        }
                      />

                      <Route
                        path="/invite-user/:token"
                        element={<UserInvite />}
                      />
                      <Route
                        path="/workspace/:id/name-list/:listId"
                        element={
                          <LoggedIn requireSales>
                            <NameList />
                          </LoggedIn>
                        }
                      />
                      <Route
                        path="/workspace/:workspaceId/transcribe-mode/:modeId"
                        element={
                          <LoggedIn requireSales>
                            <TranscribeMode />
                          </LoggedIn>
                        }
                      />
                      <Route
                        path="/latest"
                        element={
                          <LoggedIn>
                            <Latest />
                          </LoggedIn>
                        }
                      />
                      <Route
                        path="/referral-info"
                        element={
                          <LoggedIn>
                            <Referral />
                          </LoggedIn>
                        }
                      />

                      <Route
                        path="/welcome"
                        element={
                          <LoggedIn>
                            <Onboarding />
                          </LoggedIn>
                        }
                      />
                      <Route
                        path="/secure-login/verify"
                        element={<SecureLoginVerify />}
                      />
                      <Route
                        path="/secure-login/setup/:token"
                        element={<SecureLoginSetup />}
                      />
                      <Route
                        path="/customers"
                        element={
                          <LoggedIn requireSales>
                            <Customers />
                          </LoggedIn>
                        }
                      />
                      <Route
                        path="/customer/:id"
                        element={
                          <LoggedIn requireSales>
                            <Customer />
                          </LoggedIn>
                        }
                      />
                      <Route
                        path="/integrations"
                        element={
                          <LoggedIn>
                            <Integrations />
                          </LoggedIn>
                        }
                      />
                      {/* Single Tenant Admin Routes */}
                      <Route
                        path="/single-tenant/workspaces"
                        element={
                          <LoggedIn requireRole="single-tenant-admin">
                            <SingleTenantWorkspaces />
                          </LoggedIn>
                        }
                      />
                      <Route
                        path="/single-tenant/workspaces/:id"
                        element={
                          <LoggedIn requireRole="single-tenant-admin">
                            <SingleTenantWorkspace />
                          </LoggedIn>
                        }
                      />
                      <Route
                        path="/single-tenant/users"
                        element={
                          <LoggedIn requireRole="single-tenant-admin">
                            <SingleTenantUsers />
                          </LoggedIn>
                        }
                      />
                      <Route
                        path="/single-tenant/users/:id"
                        element={
                          <LoggedIn requireRole="single-tenant-admin">
                            <SingleTenantUser />
                          </LoggedIn>
                        }
                      />
                    </Routes>
                  </Suspense>
                  <UploadDialog />
                  <NPSDialog />
                  <Dialog />
                </Router>
              </AppContext.Provider>
              <SnackBar />
              <FlowRunsSnackbar />
            </GoogleOAuthProvider>
          </LocalizationProvider>
        </HelmetProvider>
      </LightProvider>
    </QueryClientProvider>
  );
}

export default App;
